import { FocusEvent, KeyboardEvent, useEffect, useState } from 'react';
import { formatCredits } from './courseMappingCellFormatters';
import { isAllowedCourseMappingCreditsDraft, parseCourseMappingCreditsInput } from './courseMappingCredits';

interface CourseMappingCreditsCellProps {
  value: number | null;
  ariaLabel: string;
  onCommit: (value: number | null) => void;
}

const toInputValue = (value: number | null): string => formatCredits(value) ?? '';

const CourseMappingCreditsCell = ({ value, ariaLabel, onCommit }: CourseMappingCreditsCellProps) => {
  const [draft, setDraft] = useState(() => toInputValue(value));
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!isFocused) {
      setDraft(toInputValue(value));
    }
  }, [value, isFocused]);

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    event.currentTarget.select();
  };

  const handleChange = (nextDraft: string) => {
    if (!isAllowedCourseMappingCreditsDraft(nextDraft)) {
      return;
    }

    setDraft(nextDraft);
  };

  const commitDraft = () => {
    const parsed = parseCourseMappingCreditsInput(draft);
    if (parsed === undefined) {
      setDraft(toInputValue(value));
      return;
    }

    onCommit(parsed);
    setDraft(toInputValue(parsed));
  };

  const handleBlur = () => {
    setIsFocused(false);
    commitDraft();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.currentTarget.blur();
      return;
    }

    if (event.key === 'Escape') {
      setDraft(toInputValue(value));
      event.currentTarget.blur();
    }
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      className={`course-mapping-credits-input${isFocused ? ' course-mapping-credits-input--focused' : ''}`}
      aria-label={ariaLabel}
      value={draft}
      onFocus={handleFocus}
      onChange={(event) => handleChange(event.target.value)}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
    />
  );
};

export default CourseMappingCreditsCell;
